import { omit } from 'lodash'
import PropTypes from 'prop-types'
import { Component, createRef } from 'react'

import List from '@/components/Form/AutoComplete/List'
import defaultProps from '@/components/Form/defaultProps'
import propTypes, { formik } from '@/components/Form/propTypes'

import './index.scss'

export default class InputList extends Component<any, any> {
  static displayName = 'InputList'

  static propTypes = {
    ...propTypes,
    formik: formik.isRequired,
    isSearching: PropTypes.bool,
    onResultClick: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    renderResult: PropTypes.func.isRequired,
    isAsyncSearch: PropTypes.bool,
    showResultsOnFocus: PropTypes.bool,
    results: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
    type: PropTypes.string,
    valueType: PropTypes.string,
    clearOnInput: PropTypes.bool,
    acceptsPropsValue: PropTypes.bool
  }

  static defaultProps = {
    ...defaultProps,
    isSearching: false,
    isAsyncSearch: true,
    showResultsOnFocus: false,
    placeholder: '',
    results: [],
    type: 'text',
    valueType: '',
    clearOnInput: true,
    acceptsPropsValue: false
  }

  ref = createRef<HTMLInputElement>()

  state = {
    isSearching: false,
    isScrollingList: false,
    isValueSelectedResult: false,
    showList: false,
    value: this.props.value || ''
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.isSearching !== prevProps.isSearching) {
      this.setState({ isSearching: this.props.isSearching })
    }

    // If value type of the input field has changed (ex. Audience Target type changes),
    // clear the text in the input field
    if (this.props.valueType !== prevProps.valueType) {
      this.onSearchChange({ target: { value: '' } })
    }

    if (this.props.value !== prevProps.value && this.props.value != null && this.props.acceptsPropsValue) {
      this.setState({ value: this.props.value })
    }
  }

  /**
   * Internet Explorer incorrectly fires a blur event when the scrollbar of the list is interacted with thereby causing
   * the list hide. To workaround the issue, we detect a mousedown event on the list's scrollbar and propagate it up
   * to here to override the blur event and instead invoke a focus event which causes the list to remain visible and
   * resets the propagated scrolling list state.
   */
  onBlur = () => {
    if (this.state.isScrollingList) {
      // Internet Explorer workaround for list scroll interactions incorrectly firing blur events
      // @ts-expect-error
      this.ref.current.focus()
    } else {
      // close the result list BEFORE marking touched as it triggers validation which could display errors
      this.setState({ showList: false }, () => this.props.formik.setFieldTouched(this.props.name))
    }
  }

  onFocus = () => {
    this.setState({ isScrollingList: false, showList: true })
  }

  /**
   * @see {@link InputList#onBlur}
   */
  onListScroll = () => this.setState({ isScrollingList: true })

  onResultClick = (result: any) => {
    const { display, value } = this.props && this.props.onResultClick(result)

    this.setState({ isValueSelectedResult: true, value: display }, () => {
      if (this.props.onChange) {
        this.props.onChange(value)
      }

      this.props.formik.setFieldValue(this.props.name, value)
    })
  }

  onSearchChange = (e: any) => {
    // user has potentially typed over a selected item value so it must be removed from formik state WITHOUT triggering validation
    if (this.props.clearOnInput) {
      this.props.formik.setFieldValue(this.props.name, '', false)
    }

    this.setState(
      {
        isSearching: e.target.value.trim().length > 0,
        showList: true,
        isValueSelectedResult: false,
        value: e.target.value.trim().length > 0 ? e.target.value : ''
      },
      () => this.props.onSearchChange(this.state.value)
    )
  }

  render() {
    // `formik`, `onChange`, etc are unused but this allows any prop to be defined by clients to be passed directly to the input
    const { showResultsOnFocus, renderResult, results, isAsyncSearch } = this.props
    const rest = omit(this.props, [
      'labelLocation',
      'formik',
      'onChange',
      'onResultClick',
      'onSearchChange',
      'renderResult',
      'results',
      'isSearching',
      'isAsyncSearch',
      'showResultsOnFocus',
      'valueType',
      'clearOnInput',
      'acceptsPropsValue'
    ])
    // only show the result list if the input is focused, has a non-empty value and the value isn't a previously selected result
    const searching = isAsyncSearch ? this.state.isSearching : false
    const showList =
      this.state.showList &&
      ((this.state.value?.toString()?.trim()?.length && !this.state.isValueSelectedResult) || showResultsOnFocus)
    return (
      <>
        <input
          {...rest}
          autoComplete="off"
          onBlur={this.onBlur}
          onChange={this.onSearchChange}
          onFocus={this.onFocus}
          ref={this.ref}
          value={this.state.value}
        />

        {!!showList && (
          <List
            isSearching={searching}
            onChange={this.onResultClick}
            onListScroll={this.onListScroll}
            renderResult={renderResult}
            results={results}
          />
        )}
      </>
    )
  }
}
